import React from 'react';

export interface HeaderProps {
  title: string;
  helpContent?: {
    title: string;
    description: string;
    content: React.ReactNode;
  };
}

// Updated header props function to include help content
export function getHeaderProps(pathname: string): HeaderProps {
  switch (pathname) {
    case '/journal':
      return { 
        title: 'Training Journal',
        helpContent: {
          title: "Training Journal Help",
          description: "Track your BJJ training progress",
          content: (
            <>
              <p>Welcome to your Training Journal! Here you can:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Log your training sessions with detailed notes</li>
                <li>Track techniques you've learned and practiced</li>
                <li>Record sparring rounds and competition results</li>
                <li>Monitor your progress over time with charts</li>
                <li>Set weekly focus areas for improvement</li>
              </ul>
              <p>Use the "Add Training" button to start logging your sessions.</p>
            </>
          )
        }
      };
    case '/techniques':
      return { 
        title: 'Technique Library',
        helpContent: {
          title: "Technique Library Help",
          description: "Browse and learn BJJ techniques",
          content: (
            <>
              <p>Welcome to the Technique Library! Here you can:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Browse thousands of BJJ techniques by position and category</li>
                <li>Watch instructional videos and view step-by-step guides</li>
                <li>Save techniques to your personal collection</li>
                <li>Filter techniques by belt level, position, and type</li>
                <li>Request new techniques to be added to the library</li>
              </ul>
              <p>Use the search and filter options to find specific techniques you want to learn.</p>
            </>
          )
        }
      };
    case '/my-techniques':
      return { 
        title: 'My Techniques',
        helpContent: {
          title: "My Techniques Help",
          description: "Manage your personal technique collection",
          content: (
            <>
              <p>Welcome to My Techniques! Here you can:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Create and save your own custom techniques</li>
                <li>Edit and organize your personal technique library</li>
                <li>Add personal notes and modifications to techniques</li>
                <li>Upload videos and images to document your techniques</li>
                <li>Share your techniques with training partners</li>
              </ul>
              <p>Use the "Create Technique" button to add new techniques to your collection.</p>
            </>
          )
        }
      };
    case '/game-planner':
    case '/game-planner2':
      return {
        title: 'Game Planner',
        helpContent: {
          title: "Game Planner Help",
          description: "Create and visualize your game plans",
          content: (
            <>
              <p>Welcome to the Game Planner! Here you can:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Create visual flowcharts of your techniques</li>
                <li>Plan competition strategies</li>
                <li>Create mental maps to help trigger reactions from any position</li>
                <li>Organize your game by positions and situations</li>
                <li>Save and share your game plans</li>
              </ul>
              <p>Use the controls to add positions, techniques, and decision points to build your plan.</p>
            </>
          )
        }
      };
    case '/referrals':
    case '/affiliates':
      return { 
        title: 'Referral Program',
        helpContent: {
          title: "Referral Program Help",
          description: "Share The Mat Monster and earn rewards",
          content: (
            <>
              <p>Welcome to our Referral Program! Here you can:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Generate unique referral links to share with others</li>
                <li>Track the performance of your referrals</li>
                <li>See your rewards and earnings</li>
                <li>Access sharing tools to promote The Mat Monster</li>
              </ul>
              <p>The more people you refer who sign up, the more rewards you earn!</p>
            </>
          )
        }
      };
    case '/profile':
      return {
        title: 'Profile',
        helpContent: {
          title: 'Profile Help',
          description: 'Manage your account settings and preferences',
          content: (
            <>
              <p>Manage your account and personalize your experience:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Update your personal information and contact details</li>
                <li>Set your belt rank and training preferences</li>
                <li>Manage notification settings</li>
                <li>View your subscription status and billing</li>
                <li>Connect with social media accounts</li>
              </ul>
              <p>Keep your profile up to date to get the most personalized training experience.</p>
            </>
          )
        }
      };
    case '/admin':
    case '/roadmap-admin':
      return { 
        title: 'Admin',
        helpContent: {
          title: 'Admin Dashboard Help',
          description: 'Manage app content and monitor system health',
          content: (
            <>
              <p>Welcome to the Admin Dashboard! Here you can:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Manage technique library content and categories</li>
                <li>Review and moderate user-generated content</li>
                <li>Monitor system performance and user activity</li>
                <li>Manage user accounts and permissions</li>
                <li>View analytics and reporting data</li>
              </ul>
              <p>Use these tools to maintain the quality and performance of The Mat Monster platform.</p>
            </>
          )
        }
      };
    default:
      return { title: 'The Mat Monster' };
  }
}
